import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { AvatarConfig } from '@shared/index'
import { api, ApiError } from '../api/http'
import { forgetPush } from '../push/device'
import type { AppLocale } from './locale'
import { usePlayerStore } from './player'

export const PASSWORD_MIN_LEN = 4

export type AuthUser = {
  id: string
  username: string
  email: string
  avatar: AvatarConfig | null
  isAdmin: boolean
}

type AuthPayload = {
  token: string
  user: AuthUser
}

type MePayload = {
  user: AuthUser
  usernameRenameAt?: string | null
}

/**
 * Session token plus the account the server issued it for.
 */
export const useAuthStore = defineStore(
  'auth',
  () => {
    const token = ref('')
    const user = ref<AuthUser | null>(null)
    /** When the next public rename is allowed. Null means it is allowed now. */
    const usernameRenameAt = ref<string | null>(null)
    const busy = ref(false)
    let sessionTask: Promise<void> | null = null

    function applyUser(next: AuthUser): void {
      user.value = {
        ...next,
        isAdmin: next.isAdmin === true,
      }
      const player = usePlayerStore()
      player.setUsername(next.username)
      if (next.avatar) player.setAvatar(next.avatar)
    }

    function clearSession(): void {
      token.value = ''
      user.value = null
      usernameRenameAt.value = null
    }

    function noteUsernameRenameAt(value: string | null): void {
      usernameRenameAt.value = value
    }

    async function register(input: {
      email: string
      username: string
      password: string
      locale?: AppLocale
    }): Promise<void> {
      busy.value = true
      try {
        const payload = await api<AuthPayload>('/auth/register', {
          method: 'POST',
          body: JSON.stringify(input),
        })
        token.value = payload.token
        applyUser(payload.user)
        if (!payload.user.avatar) {
          try {
            await patchAvatar(usePlayerStore().avatar)
          } catch {
            // Portrait stays in this browser until Profile saves it.
          }
        }
      } finally {
        busy.value = false
      }
    }

    async function login(input: { login: string; password: string }): Promise<void> {
      busy.value = true
      try {
        const payload = await api<AuthPayload>('/auth/login', {
          method: 'POST',
          body: JSON.stringify(input),
        })
        token.value = payload.token
        applyUser(payload.user)
        if (!payload.user.avatar) {
          try {
            await patchAvatar(usePlayerStore().avatar)
          } catch {
            // Portrait stays in this browser until Profile saves it.
          }
        }
      } finally {
        busy.value = false
      }
    }

    /**
     * Loads /auth/me when we have a token but no user yet.
     */
    async function ensureSession(): Promise<void> {
      if (!token.value || user.value) return
      if (!sessionTask) {
        sessionTask = fetchMe().finally(() => {
          sessionTask = null
        })
      }
      await sessionTask
    }

    async function fetchMe(): Promise<void> {
      if (!token.value) return
      try {
        const payload = await api<MePayload>('/auth/me', { token: token.value })
        applyUser(payload.user)
        usernameRenameAt.value = payload.usernameRenameAt ?? null
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.code === 'invalid_token')) {
          clearSession()
        }
      }
    }

    /**
     * Saves the portrait on the account.
     */
    async function patchAvatar(avatar: AvatarConfig): Promise<void> {
      await patchMe({ avatar })
    }

    /**
     * Saves a new public username. Issues a fresh token so chat/duels see it.
     */
    async function patchUsername(username: string): Promise<void> {
      await patchMe({ username })
    }

    async function patchMe(body: {
      avatar?: AvatarConfig
      username?: string
    }): Promise<void> {
      if (!token.value) return
      busy.value = true
      try {
        const payload = await api<MePayload & { token?: string }>('/auth/me', {
          method: 'PATCH',
          token: token.value,
          body: JSON.stringify(body),
        })
        if (payload.token) token.value = payload.token
        applyUser(payload.user)
        if (payload.usernameRenameAt !== undefined) {
          usernameRenameAt.value = payload.usernameRenameAt
        }
      } finally {
        busy.value = false
      }
    }

    async function requestPasswordReset(email: string): Promise<void> {
      busy.value = true
      try {
        await api('/auth/forgot', {
          method: 'POST',
          body: JSON.stringify({ email }),
        })
      } finally {
        busy.value = false
      }
    }

    /**
     * Saves a new password from a reset link and signs in with the new token.
     */
    async function resetPassword(input: { token: string; password: string }): Promise<void> {
      busy.value = true
      try {
        const payload = await api<AuthPayload>('/auth/reset', {
          method: 'POST',
          body: JSON.stringify(input),
        })
        token.value = payload.token
        applyUser(payload.user)
      } finally {
        busy.value = false
      }
    }

    function logout(): void {
      const bearer = token.value
      if (bearer) forgetPush(bearer)
      clearSession()
    }

    return {
      token,
      user,
      usernameRenameAt,
      busy,
      register,
      login,
      requestPasswordReset,
      resetPassword,
      ensureSession,
      fetchMe,
      noteUsernameRenameAt,
      patchAvatar,
      patchUsername,
      logout,
    }
  },
  {
    persist: {
      paths: ['token'],
    },
  },
)
